<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref, watch } from 'vue';
import Calendar from 'primevue/calendar';
import { useOverageStore, useOrganizationStore, useCustomerSuccessPartnerStore } from '@/store';
import { useRequestCancel, can } from '@/composables';
import { REQUEST, PERMISSIONS } from '@/enum';
import SeatsInUseModal from '@/views/Organizations/Connections/components/SeatsInUseModal.vue';

const overageStore = useOverageStore();
const organizationStore = useOrganizationStore();
const customerSuccessPartnerStore = useCustomerSuccessPartnerStore();
const requestCancel = useRequestCancel();
const filteredOrganizations = ref<string[]>([]);
const month = ref<Date>(new Date());
const showSeats = ref<boolean>(false);
const selectedOrganizationId = ref<number>();

const init = async () => {
  await overageStore.getOverages(month.value, filteredOrganizations.value);
};

const getOrganizationOverages = async () => {
  if (can([PERMISSIONS.OVERAGES_LIST])) {
    overageStore.loading.organization = true;
    await overageStore.getOrganizationOverages(selectedOrganizationId.value as number, month.value, false);
    overageStore.loading.organization = false;
  }
};

// Watches for changes to the CSP or Org filter on the dashboard page and makes a new API call
watch(
  [
    () => customerSuccessPartnerStore.selectedDashboardCustomerSuccessPartners,
    () => organizationStore.organizationsIdsSelected,
  ],
  async () => {
    const selectedOrgIds = new Set(organizationStore.organizationsIdsSelected.map((org) => org.toString()));
    const selectCSPOrgIds = new Set(
      customerSuccessPartnerStore.selectedDashboardCustomerSuccessPartners.flatMap((csp) =>
        csp.organizations.map((org) => org.toString()),
      ),
    );

    filteredOrganizations.value = Array.from(selectedOrgIds).concat(Array.from(selectCSPOrgIds));
    await init();
  },
);

const onDateSelect = (date: Date) => {
  month.value = date;
  init();
};

const toggleSeatsInUseModal = () => {
  showSeats.value = !showSeats.value;
};

const onOrganizationClick = async (id: number) => {
  selectedOrganizationId.value = id;
  await getOrganizationOverages();
  toggleSeatsInUseModal();
};

onMounted(async () => {
  await init();
});

onBeforeUnmount(() => requestCancel.requestSaverAbort(REQUEST.OVERAGES));
</script>

<template>
  <Card class="flex flex-column w-full px-0">
    <template #header>
      <div class="flex flex-column gap-1 pb-2">
        <div class="flex gap-1 w-full align-items-center pb-2">
          <span class="material-icons text-color-blue">supervisor_account</span>
          <h2>Seat Count Reporting</h2>
        </div>
        <SeatsInUseModal
          v-if="showSeats"
          :organization-id="selectedOrganizationId as number"
          @cancel="toggleSeatsInUseModal"
        />
        <Calendar
          v-model="month"
          view="month"
          :show-icon="true"
          :manual-input="false"
          icon-display="input"
          class="mt-3 text-sm"
          date-format="MM"
          @date-select="onDateSelect"
        />
      </div>
    </template>
    <template #content>
      <DataTable
        scrollable
        scroll-height="300px"
        :value="overageStore.dashboard_overages"
        :loading="overageStore.loading.overage"
        sort-field="overUnder"
        :sort-order="-1"
      >
        <template #empty>No Data to Display</template>
        <template #loading>Loading. Please wait.</template>
        <Column field="organization.name" header="Organization" sortable>
          <template #body="{ data }">
            <div
              class="font-semibold link"
              @click="onOrganizationClick(data.organization.id)"
              v-text="data.organization.name"
            />
          </template>
        </Column>
        <Column field="discipline.name" header="Discipline" :sortable="true">
          <template #body="{ data }">
            <div v-text="data.discipline.name" />
          </template>
        </Column>
        <Column field="overUnder" header="Seats Over/Under" class="text-center flex justify-content-center" sortable />
      </DataTable>
    </template>
  </Card>
</template>

<style scoped lang="scss">
:deep(.link) {
  color: var(--primary-color);
  text-decoration: none;
  cursor: pointer;
}
</style>
